import { useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Controller } from 'react-hook-form';
import { FormDataType } from './StyleConfiguration';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../hooks/hooksRedux';
import { RootState } from '../../../../store';
import { defaultColors } from '../../../../styles/style-guide';

type ColorInputProps = {
  name: keyof FormDataType;
  control: any;
  label: string;
  onChangePreview?: (color: string) => void;
};

export const ColorInput = ({ name, control, label, onChangePreview }: ColorInputProps) => {
  const { t } = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const initialColorRef = useRef<string>(''); 

  const { colors } = useAppSelector((state: RootState) => state.styleGuide.data);
  const secondaryColor = colors.secondary || defaultColors.secondary;


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowPicker(false);
      }
    };
    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        useEffect(() => {
          if (!initialColorRef.current && field.value) {
            initialColorRef.current = field.value;
          }
        }, [field.value]);

        return (
          <div className="relative flex flex-col gap-2 mb-6">
            <label style={{color: secondaryColor}} className="text-sm font-semibold">{label}:</label>

            <div className="flex items-center gap-3">
              <input
                type="text"
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  onChangePreview?.(e.target.value);
                }}
                onFocus={() => setShowPicker(true)}
                className="w-28 px-3 py-1 border rounded-md text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <div
                onClick={() => setShowPicker((prev) => !prev)}
                className="w-7 h-7 rounded-full border border-gray-400 cursor-pointer shadow"
                style={{ backgroundColor: field.value }}
                title={t('environment.selectColor') ?? ''}
              ></div>
              <button
                type="button"
                onClick={() => {
                  field.onChange(initialColorRef.current);
                  onChangePreview?.(initialColorRef.current);
                }}
                className="text-xs px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-100 transition"
              >
                {t('labels.lblReset')}
              </button>
            </div>

            {showPicker && (
              <div
                ref={pickerRef}
                className="absolute left-40 z-20 mt-2 p-3 bg-white border border-gray-300 rounded-lg shadow-lg"
              >
                <HexColorPicker
                  color={field.value}
                  onChange={(color) => {
                    field.onChange(color);
                    onChangePreview?.(color);
                  }}
                />
              </div>
            )}
          </div>
        );
      }}
    />
  );
};
